import { PayloadAction } from '@reduxjs/toolkit';
import { COUPON_COOKIE_NAME, removeCookie } from '../lib/util';
import { REFERRAL_COOKIE_NAME } from '../lib/util/referralCookie';
import * as types from '../types';
import { GiftShippingAddressInput, ReferralSource } from '../types/gql-op-types';

interface IRemoveCartItem {
  id: string;
}

interface IRemoveCartItems {
  ids: string[];
}

interface IUpdateGiftCartItem {
  id: string;
  isGift: boolean;
}

export function recalculateGiftStatus<T extends types.Cart>(cart: T): T {
  if (!('containsGiftItems' in cart)) {
    return cart;
  }

  const items = Object.values(cart.cartItems);
  const containsGiftItems = items.some((item) => 'isGift' in item && item.isGift);
  const containsAllGiftItems = items.every((item) => 'isGift' in item && item.isGift);

  return {
    ...cart,
    containsGiftItems,
    containsAllGiftItems,
  };
}

/**
 * Reducers that could apply to all carts (e.g. manipulating cart items)
 */
export const baseCartReducers = {
  removeCartItem<T extends types.Cart>(cart: T, { payload }: PayloadAction<IRemoveCartItem>) {
    const { [payload.id]: _, ...remainingCartItems } = cart.cartItems;

    return recalculateGiftStatus({
      ...cart,
      cartItems: remainingCartItems,
    });
  },
  removeCartItems<T extends types.Cart>(cart: T, { payload }: PayloadAction<IRemoveCartItems>) {
    let cartItems = { ...cart.cartItems };
    for (const id of payload.ids) {
      const { [id]: _, ...remainingCartItems } = cartItems;
      cartItems = remainingCartItems;
    }

    return recalculateGiftStatus({
      ...cart,
      cartItems,
    });
  },
  updateGiftCartItem<T extends types.Cart>(cart: T, { payload }: PayloadAction<IUpdateGiftCartItem>) {
    const { id, isGift } = payload;

    if (!cart.cartItems[id]) {
      return cart;
    }

    return recalculateGiftStatus({
      ...cart,
      cartItems: {
        ...cart.cartItems,
        [id]: {
          ...cart.cartItems[id],
          isGift,
        },
      },
    });
  },
  removeAllCartItems<T extends types.Cart>(cart: T) {
    return recalculateGiftStatus({
      ...cart,
      cartItems: {},
    });
  },
};

/**
 * Reducers that only apply to carts that can apply coupons
 *
 */
export const couponCartReducers = {
  addCoupon<T extends types.Cart>(cart: T, { payload: couponCode }: PayloadAction<string>) {
    // Preserve the existing cart reference when the same code is re-applied (compared case-insensitively,
    // since Recurly redeems codes case-insensitively). Returning a new object for an unchanged coupon churns
    // every `[cart]`-keyed pricing effect into re-firing /api/ecommerce/pricing, which the coupon auto-appliers
    // can turn into a runaway loop.
    if (cart.couponCode?.toLowerCase() === couponCode?.toLowerCase()) {
      return cart;
    }

    // Allow coupon code to stack with referral code
    return {
      ...cart,
      couponCode,
    };
  },
  removeCoupon<T extends types.Cart>(cart: T) {
    removeCookie(COUPON_COOKIE_NAME);

    if (cart.couponCode === undefined) {
      return cart;
    }

    return {
      ...cart,
      couponCode: undefined,
    };
  },
};

interface ReferralDetails {
  code: string;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referrerPetAvatarUrl?: string;
  source?: ReferralSource;
}

/**
 * Reducers that only apply to carts that can add referral codes
 *
 */
export const referralCodeCartReducers = {
  addReferral<T extends types.Cart>(cart: T, { payload: referralDetails }: PayloadAction<ReferralDetails>): types.Cart {
    // Allow referral code to stack with coupon code
    return {
      ...cart,
      referralCode: referralDetails.code,
      referralSource: referralDetails.source,
      referrerName: referralDetails.referrerName,
      referrerAvatarUrl: referralDetails.referrerAvatarUrl,
      referrerPetAvatarUrl: referralDetails.referrerPetAvatarUrl,
    };
  },
  removeReferral<T extends types.Cart>(cart: T): types.Cart {
    removeCookie(REFERRAL_COOKIE_NAME);

    return {
      ...cart,
      referralCode: undefined,
      referralSource: undefined,
      referrerName: undefined,
      referrerAvatarUrl: undefined,
      referrerPetAvatarUrl: undefined,
    };
  },
};

/**
 * Reducers that only apply to carts that can redeem a gift card
 *
 */
export const giftCardRedemptionCartReducers = {
  addGiftCard<T extends types.Cart>(cart: T, { payload: redeemedGiftCardCode }: PayloadAction<string>) {
    return {
      ...cart,
      redeemedGiftCardCode,
    };
  },
  removeGiftCard<T extends types.Cart>(cart: T) {
    return {
      ...cart,
      redeemedGiftCardCode: undefined,
    };
  },
};

/**
 * Reducers that only apply to carts that can add a gift shipping address
 */
export const giftShippingAddressCartReducers = {
  addGiftShippingAddress<T extends types.Cart>(
    cart: T,
    { payload: giftShippingAddress }: PayloadAction<GiftShippingAddressInput>,
  ): types.Cart {
    return {
      ...cart,
      giftShippingAddress,
    };
  },
  removeGiftShippingAddress<T extends types.Cart>(cart: T): types.Cart {
    return {
      ...cart,
      giftShippingAddress: undefined,
    };
  },
};
